import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { AppWindow, KeyRound, Plus, RotateCcw, Trash2 } from 'lucide-react';
import {
  type ClientSummary,
  type CreatedClient,
  createOAuthClient,
  deleteOAuthClient,
  listOAuthClients,
  rotateOAuthClientSecret,
  updateOAuthClient,
} from '@/api/oauthClients';
import { errorDetail } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { cn } from '@/lib/cn';

function splitLines(raw: string): string[] {
  return raw
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function OAuthClientsPage() {
  const { t } = useTranslation();
  const [clients, setClients] = useState<ClientSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  // The one-time secret to reveal (from create or rotate).
  const [secret, setSecret] = useState<{ clientId: string; clientSecret: string } | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    listOAuthClients()
      .then(setClients)
      .catch((err) => setError(errorDetail(err)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function handleDelete(id: string) {
    if (!confirm(t('oauthClients.deleteConfirm'))) return;
    try {
      await deleteOAuthClient(id);
      load();
    } catch (err) {
      setError(errorDetail(err));
    }
  }

  async function handleToggle(c: ClientSummary) {
    try {
      await updateOAuthClient(c.id, { enabled: !c.enabled });
      load();
    } catch (err) {
      setError(errorDetail(err));
    }
  }

  async function handleRotate(c: ClientSummary) {
    if (!confirm(t('oauthClients.rotateConfirm'))) return;
    try {
      const { clientSecret } = await rotateOAuthClientSecret(c.id);
      setSecret({ clientId: c.clientId, clientSecret });
    } catch (err) {
      setError(errorDetail(err));
    }
  }

  function handleCreated(created: CreatedClient) {
    setFormOpen(false);
    if (created.clientSecret) {
      setSecret({ clientId: created.clientId, clientSecret: created.clientSecret });
    }
    load();
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--color-lop-text-primary)]">
            {t('oauthClients.title')}
          </h1>
          <p className="mt-1 text-sm text-[var(--color-lop-text-secondary)]">
            {t('oauthClients.subtitle')}
          </p>
        </div>
        <Button size="sm" onClick={() => setFormOpen(true)} data-testid="oauth-client-new">
          <Plus className="mr-1.5 h-4 w-4" />
          {t('oauthClients.create')}
        </Button>
      </div>

      {error && (
        <div className="rounded-md bg-destructive/10 px-4 py-2 text-sm text-destructive">{error}</div>
      )}

      {secret && (
        <SecretBanner
          clientId={secret.clientId}
          clientSecret={secret.clientSecret}
          onDismiss={() => setSecret(null)}
        />
      )}

      {formOpen && (
        <CreateClientForm onCreated={handleCreated} onCancel={() => setFormOpen(false)} />
      )}

      {loading ? (
        <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
      ) : clients.length === 0 && !formOpen ? (
        <div className="py-12 text-center text-sm text-muted-foreground">
          {t('oauthClients.empty')}
        </div>
      ) : (
        <div className="space-y-2">
          {clients.map((c) => (
            <Card key={c.id} className="flex items-start gap-4 p-4" data-testid="oauth-client-row">
              <AppWindow className="mt-0.5 h-5 w-5 shrink-0 text-[var(--color-lop-text-muted)]" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-[var(--color-lop-text-primary)]">{c.name}</span>
                  <span
                    className={cn(
                      'rounded px-1.5 py-0.5 text-[10px] font-semibold',
                      c.enabled
                        ? 'bg-green-900/40 text-green-300'
                        : 'bg-muted text-muted-foreground',
                    )}
                  >
                    {c.enabled ? t('oauthClients.enabled') : t('oauthClients.disabled')}
                  </span>
                  <span className="rounded bg-[var(--color-lop-bg-card-hi)] px-1.5 py-0.5 text-[10px] text-[var(--color-lop-text-secondary)]">
                    {c.isPublished ? t('oauthClients.published') : t('oauthClients.unpublished')}
                  </span>
                </div>
                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                  <span>
                    {t('oauthClients.clientId')}:{' '}
                    <code className="font-mono text-[var(--color-lop-text-secondary)]">
                      {c.clientId}
                    </code>
                  </span>
                  {c.allowedScopes.length > 0 && (
                    <span>
                      {t('oauthClients.scopes')}:{' '}
                      <code className="font-mono">{c.allowedScopes.join(' ')}</code>
                    </span>
                  )}
                </div>
                {c.redirectUris.length > 0 && (
                  <div className="mt-1 text-xs text-muted-foreground">
                    {t('oauthClients.redirectUris')}:{' '}
                    <span className="break-all">{c.redirectUris.join(', ')}</span>
                  </div>
                )}
              </div>
              <div className="flex shrink-0 gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleToggle(c)}
                  data-testid="oauth-client-toggle"
                >
                  {c.enabled ? t('oauthClients.disable') : t('oauthClients.enable')}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  title={t('oauthClients.rotateSecret')}
                  onClick={() => handleRotate(c)}
                  data-testid="oauth-client-rotate"
                >
                  <RotateCcw className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  title={t('common.delete')}
                  onClick={() => handleDelete(c.id)}
                  data-testid="oauth-client-delete"
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function SecretBanner({
  clientId,
  clientSecret,
  onDismiss,
}: {
  clientId: string;
  clientSecret: string;
  onDismiss: () => void;
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(clientSecret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable — the field stays selectable */
    }
  }

  return (
    <Card tier="raised" className="border-amber-500/40 bg-amber-500/5" data-testid="oauth-secret-banner">
      <div className="flex items-center gap-2">
        <KeyRound className="h-4 w-4 text-amber-400" />
        <h3 className="text-sm font-semibold text-amber-300">{t('oauthClients.secret.title')}</h3>
      </div>
      <p className="mt-1 text-sm text-[var(--color-lop-text-secondary)]">
        {t('oauthClients.secret.warning')}
      </p>
      <div className="mt-3 text-xs text-muted-foreground">
        {t('oauthClients.clientId')}:{' '}
        <code className="font-mono text-[var(--color-lop-text-secondary)]">{clientId}</code>
      </div>
      <div className="mt-2 flex gap-2">
        <Input
          value={clientSecret}
          readOnly
          onFocus={(e) => e.currentTarget.select()}
          className="font-mono"
        />
        <Button size="sm" onClick={copy} data-testid="oauth-secret-copy">
          {copied ? t('common.copied') : t('common.copy')}
        </Button>
      </div>
      <div className="mt-3 flex justify-end">
        <Button size="sm" variant="outline" onClick={onDismiss}>
          {t('common.close')}
        </Button>
      </div>
    </Card>
  );
}

function CreateClientForm({
  onCreated,
  onCancel,
}: {
  onCreated: (c: CreatedClient) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [redirectUris, setRedirectUris] = useState('');
  const [scopes, setScopes] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [logoUrl, setLogoUrl] = useState('');
  const [homepageUrl, setHomepageUrl] = useState('');
  const [privacyPolicyUrl, setPrivacyPolicyUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const created = await createOAuthClient({
        name: name.trim(),
        redirectUris: splitLines(redirectUris),
        allowedScopes: splitLines(scopes),
        isPublic,
        logoUrl: logoUrl.trim() || null,
        homepageUrl: homepageUrl.trim() || null,
        privacyPolicyUrl: privacyPolicyUrl.trim() || null,
      });
      onCreated(created);
    } catch (err) {
      setError(errorDetail(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card className="p-5">
      <h3 className="mb-4 font-medium">{t('oauthClients.createModal.title')}</h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}
        <div>
          <label className="mb-1 block text-xs font-medium">{t('oauthClients.name')}</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">{t('oauthClients.redirectUris')}</label>
          <Textarea
            value={redirectUris}
            onChange={(e) => setRedirectUris(e.target.value)}
            rows={2}
            placeholder={t('oauthClients.redirectUrisPlaceholder')}
          />
          <p className="mt-1 text-[11px] text-muted-foreground">
            {t('oauthClients.redirectUrisHint')}
          </p>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">{t('oauthClients.scopes')}</label>
          <Input
            value={scopes}
            onChange={(e) => setScopes(e.target.value)}
            placeholder={t('oauthClients.scopesPlaceholder')}
          />
        </div>
        <div className="flex items-center gap-2">
          <input
            id="oauth-public"
            type="checkbox"
            checked={isPublic}
            onChange={(e) => setIsPublic(e.target.checked)}
            className="h-4 w-4 rounded border-input"
          />
          <label htmlFor="oauth-public" className="text-sm">
            {t('oauthClients.isPublic')}
          </label>
        </div>
        <p className="text-[11px] text-muted-foreground">
          {isPublic ? t('oauthClients.publicHint') : t('oauthClients.confidentialHint')}
        </p>
        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <label className="mb-1 block text-xs font-medium">{t('oauthClients.logoUrl')}</label>
            <Input value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} placeholder="https://..." />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium">{t('oauthClients.homepageUrl')}</label>
            <Input
              value={homepageUrl}
              onChange={(e) => setHomepageUrl(e.target.value)}
              placeholder="https://..."
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium">
              {t('oauthClients.privacyPolicyUrl')}
            </label>
            <Input
              value={privacyPolicyUrl}
              onChange={(e) => setPrivacyPolicyUrl(e.target.value)}
              placeholder="https://..."
            />
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onCancel} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={saving || !name.trim()} data-testid="oauth-client-create">
            {saving ? t('common.saving') : t('oauthClients.createModal.submit')}
          </Button>
        </div>
      </form>
    </Card>
  );
}
